import { Fragment, useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router"
import { toast } from "sonner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { apple } from "@/shortcuts/keys.ts"
import { shortcutKeys, type ShortcutId } from "@/shortcuts/registry.ts"
import { keyShortcutsOf } from "./key.ts"
import { useViewport } from "./viewport.tsx"

/*
 * Two ways of writing a thing down that a Member reads exactly: a Key, the identifier of a Project
 * or a ticket (OPS, VRF-42), and a Kbd, the keys that do something.
 */

/** How long "Copied" stays in a tooltip before it says what a click copies again. */
const copiedFor = 1000

/**
 * Copies a value on demand, and says so for a second. On a desk there is no toast: the tooltip
 * that offered the copy says it was done, where the Member is looking. On touch, which shows no
 * tooltips, a toast says "Copied". A clipboard the browser refuses leaves the offer standing.
 */
export function useCopied(value: string) {
  const { touch } = useViewport()
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])
  const copy = () =>
    navigator.clipboard.writeText(value).then(
      () => {
        if (touch) toast.success("Copied")
        setCopied(true)
        clearTimeout(timer.current)
        timer.current = setTimeout(() => setCopied(false), copiedFor)
      },
      () => undefined,
    )
  return { copied, copy }
}

/**
 * Copies `text` where no tooltip is there to say so, from a menu or a key, and says it in a toast:
 * `said` once the browser has taken it, or the one sentence every copy uses when it refuses. Gives
 * back whether it was copied.
 */
export function copyText(text: string, said: string) {
  return navigator.clipboard.writeText(text).then(
    () => {
      toast.success(said)
      return true
    },
    () => {
      toast.error("The browser wouldn't copy that. Select it and copy it instead.")
      return false
    },
  )
}

const keyText = "text-xs font-medium whitespace-nowrap text-muted-foreground tabular-nums shrink-0"

/**
 * A Project's or a ticket's key, in the secondary tier (the faint one fails contrast at this size).
 * `copy` makes it a button that copies it, its tooltip saying "Copy VRF-4" and then "Copied";
 * `href` makes it a link, opening in place of the page or, with `opens="new-tab"`, as inside a
 * ticket on the desk, in a new browser tab; `state` goes with a link that opens in place, for the
 * page it opens.
 */
export function Key({
  value,
  copy,
  href,
  opens = "in-place",
  state,
}: {
  value: string
  copy?: boolean
  href?: string
  opens?: "in-place" | "new-tab"
  state?: unknown
}) {
  if (copy) return <CopyableKey value={value} />
  if (href)
    return (
      <Link
        to={href}
        {...(state !== undefined && { state })}
        {...(opens === "new-tab" && { target: "_blank", rel: "noopener" })}
        className={cn(
          keyText,
          "rounded-sm transition-colors duration-100 ease-snappy hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        )}
      >
        {value}
      </Link>
    )
  return <span className={keyText}>{value}</span>
}

function CopyableKey({ value }: { value: string }) {
  const { copied, copy } = useCopied(value)
  return (
    <Tooltip>
      <TooltipTrigger
        closeOnClick={false}
        onClick={copy}
        className={cn(
          keyText,
          "cursor-pointer rounded-sm transition-colors duration-100 ease-snappy touch-target hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        )}
      >
        {value}
      </TooltipTrigger>
      <TooltipContent>{copied ? "Copied" : `Copy ${value}`}</TooltipContent>
    </Tooltip>
  )
}

/**
 * The modifier chips as a Mac writes them, and as everywhere else does. A Kbd given ⌘ shows Ctrl
 * off a Mac, so a caller writes one set of keys for both.
 */
const offApple: Record<string, string> = { "⌘": "Ctrl", "⌥": "Alt" }

const chipClass =
  "inline-flex h-5 min-w-5 items-center justify-center rounded-sm bg-muted px-1 font-sans text-2xs font-medium text-muted-foreground tabular-nums ring-1 ring-border-subtle ring-inset"

// What a Kbd can decorate: a control whose accessible name would otherwise take its chips in.
const controls =
  "button, a, [role=button], [role=link], [role=option], [role=menuitem], [role=menuitemradio], [role=menuitemcheckbox], [role=tab]"

/**
 * Keys on chips: one chip a key, and a chord's two strokes with "then" between them. Pass
 * `shortcut`, a registry id, to show that shortcut's keys wherever it is described, or `keys` for
 * one stroke that is not a registered shortcut, such as ⌘↵ in a form. Inside a solid Button or a
 * Tooltip the chips invert onto its fill. The whole Kbd is the `kbd-group` slot and each chip
 * `kbd`, so a Button spaces the Kbd from its label and styles the chips one by one.
 *
 * Inside a control — a Button, a palette option — the chips would join its name ("Create ticket
 * Ctrl ↵"), read out as symbol names. There they are hidden from screen readers, and the control
 * carries the shortcut in `aria-keyshortcuts` instead, which a chord can't be written in. Where
 * the Kbd is the content, as in the Shortcuts dialog, it is read as it stands.
 *
 * On touch the chips are gone, since there is no keyboard to press them on. Only the span is
 * hidden, so it still writes `aria-keyshortcuts` on its control for a keyboard attached later.
 * `always` keeps them where the keys are the content, as in the Shortcuts dialog, which a tablet
 * with a keyboard opens.
 */
export function Kbd(
  props: ({ shortcut: ShortcutId } | { keys: readonly string[] }) & { always?: boolean },
) {
  const strokes =
    "shortcut" in props
      ? shortcutKeys(props.shortcut)
      : [props.keys.map((key) => (apple ? key : (offApple[key] ?? key)))]
  const named = keyShortcutsOf(strokes)
  // Where it sits is only known once it is in the page. The attributes are ones React never sets
  // on these elements, so it leaves them alone.
  const decorate = useCallback(
    (chips: HTMLSpanElement | null) => {
      const control = chips?.parentElement?.closest(controls)
      if (!chips || !control) return
      chips.setAttribute("aria-hidden", "true")
      if (!named || control.hasAttribute("aria-keyshortcuts")) return
      control.setAttribute("aria-keyshortcuts", named)
      return () => control.removeAttribute("aria-keyshortcuts")
    },
    [named],
  )
  return (
    <span
      ref={decorate}
      data-slot="kbd-group"
      className={cn("inline-flex shrink-0 items-center gap-0.5", !props.always && "touch:hidden")}
    >
      {strokes.map((chips, stroke) => (
        <Fragment key={stroke}>
          {stroke > 0 && (
            <span data-slot="kbd-then" className="px-0.5 text-2xs text-subtle-foreground">
              then
            </span>
          )}
          {chips.map((chip, index) => (
            <kbd key={index} data-slot="kbd" className={chipClass}>
              {chip}
            </kbd>
          ))}
        </Fragment>
      ))}
    </span>
  )
}
